/**
 * Twilio Service
 * Handles communication with the Twilio API via our backend
 */
import { toast } from 'react-toastify';
import { authenticatedFetch } from '../../api/apiClient';
import { Device } from '@twilio/voice-sdk';
import { buildMicConstraints } from '../../utils/browserAudioDevices';

let twilioDeviceInstance = null;
let twilioDeviceIdentity = '';
let twilioDeviceRegistered = false;
let twilioDeviceRegistrationPromise = null;

function getCspConnectSrc() {
  try {
    const meta = document.querySelector("meta[http-equiv='Content-Security-Policy']");
    const content = meta?.getAttribute('content') || '';
    if (!content) return null;
    const directive = content
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith('connect-src'));
    return directive || null;
  } catch (_) {
    return null;
  }
}

function getRuntimeNetworkContext() {
  return {
    appOrigin: window.location.origin,
    appHost: window.location.host,
    pageProtocol: window.location.protocol,
    isOnline: navigator.onLine,
    userAgent: navigator.userAgent,
    cspConnectSrc: getCspConnectSrc(),
  };
}

function resolveTwilioEdge() {
  const configured = (import.meta?.env?.VITE_TWILIO_EDGE || '').trim();
  if (!configured) {
    return 'ashburn';
  }

  if (configured.includes(',')) {
    const edges = configured
      .split(',')
      .map((edge) => edge.trim())
      .filter(Boolean);
    return edges.length > 0 ? edges : 'ashburn';
  }

  return configured;
}

export function getTwilioDeviceInstance() {
  return twilioDeviceInstance;
}

// Use relative paths with Firebase Hosting rewrites
// No need for API base URL with relative paths

/**
 * Send an SMS message via Twilio
 * @param {Object} params - SMS parameters
 * @param {string} params.to - Recipient phone number (E.164 format)
 * @param {string} params.message - SMS message content
 * @returns {Promise<Object>} - Response data
 */
export async function sendSms({ to, message }) {
  try {
    console.log('🔄 Sending SMS to:', to);
    const response = await authenticatedFetch('/api/test/send-sms', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to, message }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Server responded with ${response.status}: ${text}`);
    }
    const data = await response.json();
    console.log('✅ SMS sent successfully:', data);
    return data;
  } catch (error) {
    console.error('❌ Error sending SMS:', error);

    // Extract error message
    const errorMessage = error.message || 'Failed to send SMS';
    toast.error(`SMS Error: ${errorMessage}`);
    throw error;
  }
}

async function applySipInputDevice(device, micDeviceId) {
  if (!device?.audio) return { usedDeviceId: '', fallback: false };
  try {
    if (micDeviceId) {
      await device.audio.setInputDevice(micDeviceId);
      return { usedDeviceId: micDeviceId, fallback: false };
    }
    if (typeof device.audio.unsetInputDevice === 'function') {
      await device.audio.unsetInputDevice();
    }
    return { usedDeviceId: '', fallback: false };
  } catch (error) {
    // Paging-only path. A stale or missing device id must not block the page
    // after authorization. Calling keeps its own input-device handling.
    console.warn('[Twilio] Selected microphone unavailable; falling back to default input', {
      micDeviceId: micDeviceId || null,
      message: error?.message || String(error),
    });
    try {
      if (typeof device.audio.unsetInputDevice === 'function') {
        await device.audio.unsetInputDevice();
      }
    } catch (_) {
      /* default input remains usable */
    }
    return { usedDeviceId: '', fallback: true };
  }
}

export async function startSipCall(options = {}) {
  const authToken = options.authToken;
  const micDeviceId = typeof options.micDeviceId === 'string' ? options.micDeviceId.trim() : '';

  if (!authToken || typeof authToken !== 'string') {
    throw new Error('Missing SIP authorization token');
  }

  // Outbound paging must not register a second incoming Device for the user identity.
  const device = await initializeTwilioDevice({ registerForIncoming: false });
  const micResult = await applySipInputDevice(device, micDeviceId);

  console.log('Attempting device.connect()', {
    micRequested: Boolean(micDeviceId),
    micApplied: Boolean(micResult.usedDeviceId),
    micFallback: Boolean(micResult.fallback),
  });

  const connectOpts = {
    params: { t: authToken },
  };
  if (micResult.usedDeviceId) {
    connectOpts.rtcConstraints = { audio: buildMicConstraints(micResult.usedDeviceId) };
  }
  const call = await device.connect(connectOpts);

  if (call && !call._canalertsSipErrorListenerAttached) {
    call._canalertsSipErrorListenerAttached = true;
    call.on('error', (error) => {
      console.error('CALL ERROR FULL', {
        code: error?.code,
        message: error?.message,
        raw: error,
      });
    });
  }

  return call;
}

/**
 * Place a SIP call via Twilio
 * @param {Object} params - Call parameters
 * @param {string} params.to - SIP address or phone number (this will be mapped to 'target' for the API)
 * @param {string} params.message - Message to be spoken during the call
 * @param {string} [params.messageType='tts'] - Message type (tts or audio)
 * @param {string} [params.ttsLanguage='en-US'] - TTS language
 * @param {string} [params.tenantId] - Tenant ID (sent as tenantId and X-Tenant-ID)
 * @returns {Promise<Object>} - Response data
 */
export async function placeSipCall({
  to,
  message,
  messageType = 'tts',
  ttsLanguage = 'en-US',
  tenantId = null,
}) {
  try {
    console.log('🔄 Placing SIP call to:', to);

    // Map 'to' to 'target' as required by the backend
    // Prefer canonical endpoint; fall back to legacy alias if needed
    const payload = {
      target: to,
      message,
      messageType,
      ttsLanguage,
    };
    if (tenantId) {
      payload.tenantId = tenantId;
    }

    const tryRequest = async (url) => {
      const headers = { 'Content-Type': 'application/json' };
      if (tenantId) {
        headers['X-Tenant-ID'] = tenantId;
      }
      const res = await authenticatedFetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      return res;
    };

    let response = await tryRequest('/api/test/send-sip-call');
    if (response.status === 404) {
      // Fallback to alias route if canonical not present on backend
      response = await tryRequest('/api/test/sip-call');
    }
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Server responded with ${response.status}: ${text}`);
    }
    const data = await response.json();
    if (!data?.success) {
      throw new Error(data?.error || data?.message || 'SIP call was not placed');
    }
    console.log('✅ SIP call placed successfully:', data);
    return data;
  } catch (error) {
    console.error('❌ Error placing SIP call:', error);

    // Extract error message
    const errorMessage = error.message || 'Failed to place SIP call';
    toast.error(`SIP Call Error: ${errorMessage}`);
    throw error;
  }
}

/**
 * Fetch the most recent incoming SIP call data
 * @returns {Promise<Object>} - SIP call data
 */
export async function getLastIncomingSipCall() {
  try {
    console.log('🔄 Fetching last incoming SIP call data');
    const response = await authenticatedFetch('/api/test/last-incoming-sip', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Server responded with ${response.status}: ${text}`);
    }
    const data = await response.json();

    if (data && data.call) {
      console.log('✅ SIP call data retrieved successfully');
      return data.call;
    } else {
      console.warn('⚠️ No SIP call data found');
      return null;
    }
  } catch (error) {
    console.error('❌ Error fetching SIP call data:', error);

    // Extract error message
    const errorMessage = error.message || 'Failed to fetch SIP call data';
    toast.error(`SIP Data Error: ${errorMessage}`);
    throw error;
  }
}

export async function initializeTwilioDevice(options = {}) {
  const registerForIncoming = options.registerForIncoming !== false;
  // Server derives identity from the authenticated UID; client cannot select it.
  const identityKey = registerForIncoming ? 'incoming' : 'outbound-only';

  if (twilioDeviceInstance && twilioDeviceIdentity === identityKey) {
    if (registerForIncoming && !twilioDeviceRegistered) {
      if (!twilioDeviceRegistrationPromise) {
        twilioDeviceRegistrationPromise = twilioDeviceInstance.register()
          .then(() => {
            twilioDeviceRegistered = true;
            console.log('Device registration complete');
          })
          .catch((registerError) => {
            twilioDeviceRegistrationPromise = null;
            throw registerError || new Error('Twilio device registration failed');
          });
      }
      await twilioDeviceRegistrationPromise;
    }
    return twilioDeviceInstance;
  }

  try {
    const response = await authenticatedFetch('/api/twilio/voice-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Server responded with ${response.status}: ${text}`);
    }

    const data = await response.json();
    const token = data && data.token;

    if (!token || typeof token !== 'string') {
      throw new Error('Missing token in voice-token response');
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expEpochMs = typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
      const tokenIdentity = payload?.grants?.identity || null;
      console.log('VOICE TOKEN PAYLOAD', payload);
      console.log('VOICE TOKEN CHECK', {
        hasIdentity: Boolean(tokenIdentity),
        identityFromGrant: tokenIdentity,
        hasVoiceOutgoingApplicationSid: Boolean(payload?.grants?.voice?.outgoing?.application_sid),
        outgoingApplicationSid: payload?.grants?.voice?.outgoing?.application_sid || null,
        tokenAccountSid: payload?.sub || null,
        tokenApiKeySid: payload?.iss || null,
        tokenRegion: payload?.twr || null,
        exp: payload?.exp,
        expIso: expEpochMs ? new Date(expEpochMs).toISOString() : null,
        isExpired: expEpochMs ? expEpochMs <= Date.now() : null,
      });
      console.log(
        'VOICE TOKEN CLAIMS SUMMARY',
        `identity=${tokenIdentity || 'null'} | sub=${payload?.sub || 'null'} | iss=${payload?.iss || 'null'} | appSid=${payload?.grants?.voice?.outgoing?.application_sid || 'null'} | twr=${payload?.twr || 'null'} | exp=${payload?.exp || 'null'}`
      );
    } catch (payloadError) {
      console.error('VOICE TOKEN PAYLOAD PARSE FAILED', payloadError);
    }

    const twilioEdge = resolveTwilioEdge();
    console.log('TWILIO DEVICE OPTIONS', {
      edge: twilioEdge,
      hasEnvOverride: Boolean((import.meta?.env?.VITE_TWILIO_EDGE || '').trim()),
      registerForIncoming,
    });

    const device = new Device(token, {
      codecPreferences: ['opus', 'pcmu'],
      debug: false,
      edge: twilioEdge,
    });

    if (!device._canalertsLifecycleListenersAttached) {
      device._canalertsLifecycleListenersAttached = true;

      device.on('ready', () => {
        console.log('Twilio Device READY');
      });

      device.on('registered', () => {
        console.log('Twilio Device REGISTERED');
      });

      device.on('unregistered', () => {
        console.log('Twilio Device UNREGISTERED');
        twilioDeviceRegistered = false;
        twilioDeviceRegistrationPromise = null;
      });

      device.on('offline', () => {
        console.log('Twilio Device OFFLINE');
        twilioDeviceRegistered = false;
        twilioDeviceRegistrationPromise = null;
      });

      device.on('error', (error) => {
        console.error('DEVICE ERROR FULL', {
          code: error?.code,
          message: error?.message,
          stack: error?.stack,
          raw: error,
        });

        if (error?.code === 53000) {
          console.error('TWILIO SIGNALING CONTEXT', {
            ...getRuntimeNetworkContext(),
            identity: twilioDeviceIdentity || null,
            registeredState: twilioDeviceRegistered,
            hasPendingRegisterPromise: Boolean(twilioDeviceRegistrationPromise),
            causes: error?.causes || null,
            originalError: error?.originalError || null,
          });
          console.error(
            'TWILIO SIGNALING CHECKLIST',
            'Verify token sub/iss/appSid account alignment, CSP connect-src for Twilio, and websocket access from this network.'
          );
        }
      });
    }

    twilioDeviceInstance = device;
    twilioDeviceIdentity = identityKey;
    twilioDeviceRegistered = false;
    twilioDeviceRegistrationPromise = null;

    if (registerForIncoming) {
      twilioDeviceRegistrationPromise = device.register()
        .then(() => {
          twilioDeviceRegistered = true;
          console.log('Device registration complete');
        })
        .catch((registerError) => {
          twilioDeviceRegistrationPromise = null;
          throw registerError || new Error('Twilio device registration failed');
        });

      await twilioDeviceRegistrationPromise;
    }

    return device;
  } catch (error) {
    console.error('Error initializing Twilio Device', error);
    const errorMessage = error?.message || 'Failed to initialize Twilio Device';
    toast.error(errorMessage);
    throw error || new Error(errorMessage);
  }
}

